/**
 * Ticket Selector Component - select tickets for raffle
 */

import { useState, useEffect, useRef } from "react";
import { IconTicket, IconCheck } from "@/components/Icons";
import { haptic } from "@/lib/telegram";
import {
  useAdminRaffleTickets,
  useRaffleTickets,
  type RaffleTicket,
  type TicketFilter,
} from "../hooks";

const FILTER_OPTIONS: { value: TicketFilter; label: string }[] = [
  { value: "PAID", label: "Оплаченные" },
  { value: "USED", label: "Только пришли" },
  { value: "ALL", label: "Все (вкл. неоплаченные)" },
];

interface TicketSelectorProps {
  raffleId: string;
  eventId: string;
  canEdit: boolean;
  onSave?: () => void;
}

export function TicketSelector({
  raffleId,
  eventId,
  canEdit,
  onSave,
}: TicketSelectorProps) {
  const [filter, setFilter] = useState<TicketFilter>("PAID");
  const { tickets: raffleTickets, eventTickets, isLoading, refetch } =
    useAdminRaffleTickets(raffleId, eventId, filter);
  const { setTickets, isLoading: isSaving } = useRaffleTickets();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);

  // Update selection when raffle tickets load (only once after initial load)
  useEffect(() => {
    if (raffleTickets.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      setSelectedIds(new Set(raffleTickets.map((t: RaffleTicket) => t.id)));
    }
  }, [raffleTickets]);

  const handleToggle = (ticketId: string) => {
    if (!canEdit) return;
    haptic("light");

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      await setTickets({ raffleId, ticketIds: Array.from(selectedIds) });
      haptic("success");
      refetch();
      onSave?.(); // Notify parent to refresh raffle data
    } catch (err) {
      haptic("error");
    }
  };

  const handleSelectAll = () => {
    haptic("light");
    setSelectedIds(new Set(eventTickets.map((t: RaffleTicket) => t.id)));
  };

  const handleSelectNone = () => {
    haptic("light");
    setSelectedIds(new Set());
  };

  const selectedCount = selectedIds.size;
  const originalIds = new Set(raffleTickets.map((t: RaffleTicket) => t.id));
  const hasChanges = !setsEqual(selectedIds, originalIds);

  // Filter UI component (shown always, even when loading or no results)
  const filterUI = (
    <div className="card p-3">
      <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
        Фильтр билетов
      </label>
      <div className="flex flex-wrap gap-2">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              haptic("light");
              setFilter(opt.value);
            }}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === opt.value
                ? "bg-purple-600 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-3">
        {filterUI}
        <div className="card p-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-500">Загрузка билетов...</p>
          </div>
        </div>
      </div>
    );
  }

  if (eventTickets.length === 0) {
    return (
      <div className="space-y-3">
        {filterUI}
        <div className="card p-6 text-center">
          <IconTicket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <h3 className="font-medium text-slate-700 mb-2">Нет билетов</h3>
          <p className="text-sm text-slate-500">
            {filter === "ALL" 
              ? "Для розыгрыша нужны проданные билеты"
              : `Нет билетов по фильтру "${FILTER_OPTIONS.find(o => o.value === filter)?.label}"`
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Filter */}
      {filterUI}

      {/* Stats and actions */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-2xl font-bold text-slate-900">
              {selectedCount}
            </span>
            <span className="text-sm text-slate-500 ml-1">
              из {eventTickets.length} выбрано
            </span>
            {originalIds.size > 0 && (
              <span className="text-xs text-purple-600 ml-2">
                ({originalIds.size} в розыгрыше)
              </span>
            )}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="btn-secondary text-sm"
              >
                Все
              </button>
              <button
                onClick={handleSelectNone}
                className="btn-secondary text-sm"
              >
                Никто
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ticket list */}
      <div className="card divide-y divide-slate-100 max-h-96 overflow-y-auto">
        {eventTickets.map((ticket: RaffleTicket) => (
          <TicketRow
            key={ticket.id}
            ticket={ticket}
            isSelected={selectedIds.has(ticket.id)}
            isInRaffle={originalIds.has(ticket.id)}
            canEdit={canEdit}
            onToggle={() => handleToggle(ticket.id)}
          />
        ))}
      </div>

      {/* Save button */}
      {canEdit && hasChanges && (
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn-primary w-full"
        >
          Сохранить изменения
        </button>
      )}
    </div>
  );
}

interface TicketRowProps {
  ticket: RaffleTicket;
  isSelected: boolean;
  isInRaffle: boolean;
  canEdit: boolean;
  onToggle: () => void;
}

function TicketRow({ ticket, isSelected, isInRaffle, canEdit, onToggle }: TicketRowProps) {
  return (
    <button
      onClick={onToggle}
      disabled={!canEdit}
      className={`w-full p-3 flex items-center gap-3 hover:bg-slate-50 disabled:hover:bg-white transition-colors text-left ${
        isInRaffle ? "bg-purple-50" : ""
      }`}
    >
      {/* Checkbox */}
      <div
        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
          isSelected
            ? "bg-purple-500 border-purple-500"
            : "border-slate-300"
        }`}
      >
        {isSelected && <IconCheck className="w-3 h-3 text-white" />}
      </div>

      {/* Ticket number */}
      <div className={`font-mono text-sm font-medium px-2 py-1 rounded flex-shrink-0 ${
        isInRaffle ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-900"
      }`}>
        #{ticket.ticketNumber}
      </div>

      {/* Owner info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700 truncate">{ticket.accountName}</p>
        {ticket.accountUsername && (
          <p className="text-xs text-slate-500">@{ticket.accountUsername}</p>
        )}
      </div>

      {/* Status badges */}
      <div className="flex gap-1 flex-shrink-0">
        {isInRaffle && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
            🎲
          </span>
        )}
        {ticket.isPaid && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700">
            💰
          </span>
        )}
        {ticket.isUsed && (
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
            ✓
          </span>
        )}
      </div>

      {/* Ticket type */}
      <span className="text-xs text-slate-500 flex-shrink-0 hidden sm:block">{ticket.ticketTypeName}</span>
    </button>
  );
}

function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}
